import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const getAuthErrorMessage = (error, fallback = 'Une erreur est survenue. Veuillez réessayer.') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error.error) return error.error;
  if (error.message) return error.message;

  const validationErrors = error.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const firstMessages = Object.values(validationErrors).flat();
    if (firstMessages.length) return firstMessages.join(' ');
  }

  return fallback;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('userData');

      if (storedToken && storedUser) {
        // Verify token validity
        try {
          const response = await authAPI.getProfile();
          if (response.success) {
            setUser(response.user);
            setToken(storedToken);
            setIsAuthenticated(true);
          }
        } catch {
          clearAuth();
        }
      }
    } catch (error) {
      console.error('Auth init error:', error);
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        setIsAuthenticated(true);
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userData', JSON.stringify(response.user));
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (error) {
      return { success: false, error: getAuthErrorMessage(error, 'La connexion a échoué. Veuillez réessayer.') };
    }
  };

  const register = async (name, email, password, role = 'user') => {
    try {
      const response = await authAPI.register(name, email, password, role);
      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        setIsAuthenticated(true);
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userData', JSON.stringify(response.user));
        return { success: true };
      }
      return { success: false, error: getAuthErrorMessage(response, "L'inscription a échoué. Veuillez réessayer.") };
    } catch (error) {
      return { success: false, error: getAuthErrorMessage(error, "L'inscription a échoué. Veuillez réessayer.") };
    }
  };

  const logout = async () => {
    try {
      if (token) await authAPI.logout();
    } finally {
      clearAuth();
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authAPI.refresh();
      if (response.success) {
        setToken(response.token);
        localStorage.setItem('authToken', response.token);
        return response.token;
      }
      throw new Error('Actualisation échouée');
    } catch (error) {
      clearAuth();
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, isAuthenticated, login, register, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
